import { useState, useEffect, useRef } from 'react'
import H from '../data'
import HI from './Icons'

const money = H.fmt;

/* ---------- nav config ---------- */
const NAV = [
  { group: "Overview", items: [{ id: "dashboard", label: "Dashboard", icon: "dash" }] },
  { group: "Operations", items: [
    { id: "bookings", label: "Bookings", icon: "calendar", badge: 3 },
    { id: "availability", label: "Availability", icon: "clock" },
    { id: "pricing", label: "Pricing", icon: "tag" },
  ]},
  { group: "Listing", items: [
    { id: "workspace", label: "Workspace", icon: "store" },
    { id: "reviews", label: "Reviews", icon: "star", dot: true },
  ]},
  { group: "Business", items: [{ id: "analytics", label: "Revenue & analytics", icon: "chart" }] },
];

/* ---------- small atoms ---------- */
function Av({ name, initials, c = 0, size = "md", className = "" }) {
  const ini = initials || (name ? name.split(" ").map((s) => s[0]).slice(0, 2).join("") : "?");
  return <div className={`av av-c${c % 6} ${size} ${className}`}>{ini}</div>;
}

function Stars({ value, size = 14 }) {
  const full = Math.round(value);
  return (
    <span className="stars" aria-label={value + " stars"}>
      {[1, 2, 3, 4, 5].map((i) => (
        <HI.star key={i} s={size} fill="none" style={{ color: i <= full ? "var(--amber)" : "var(--border-3)" }} />
      ))}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    "upcoming": ["blue", "Upcoming"],
    "checked-in": ["green", "Checked in"],
    "completed": ["gray", "Completed"],
    "cancelled": ["red", "Cancelled"],
    "no-show": ["amber", "No-show"],
    "web": ["gray", "Web"],
  };
  const [tone, label] = map[status] || ["gray", status];
  return <span className={`badge ${tone}`}><span className="bd" />{label}</span>;
}

function Delta({ value, suffix = "%", invert = false }) {
  const up = value >= 0;
  const good = invert ? !up : up;
  return (
    <span className={`delta ${value === 0 ? "flat" : good ? "up" : "down"}`}>
      {value !== 0 && (up ? <HI.trend s={12} /> : <HI.trendDown s={12} />)}
      {up ? "+" : ""}{value}{suffix}
    </span>
  );
}

/* ---------- KPI ---------- */
function Kpi({ label, icon, value, footer }) {
  return (
    <div className="kpi">
      <div className="lbl">{icon}{label}</div>
      <div className="val">{value}</div>
      <div className="foot">{footer}</div>
    </div>
  );
}

/* ---------- banner ---------- */
function Banner({ tone = "blue", icon, title, children, action }) {
  return (
    <div className={`banner ${tone}`}>
      <span className="bi">{icon}</span>
      <div className="grow">
        {title && <div className="bt">{title}</div>}
        <div className="bd">{children}</div>
      </div>
      {action}
    </div>
  );
}

/* ---------- empty state ---------- */
function Empty({ icon, title, desc, action }) {
  return (
    <div className="empty">
      <div className="ei">{icon}</div>
      <div className="et">{title}</div>
      {desc && <div className="ed">{desc}</div>}
      {action && <div className="ea">{action}</div>}
    </div>
  );
}

/* ---------- skeleton table ---------- */
function SkeletonRows({ rows = 5, cols = 4 }) {
  return (
    <div style={{ padding: "6px 0" }}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="row g16" style={{ padding: "13px 14px", borderBottom: "1px solid var(--border)" }}>
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="sk" style={{ height: 14, flex: c === 0 ? 2 : 1 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ---------- bar chart ---------- */
function BarChart({ data, valueKey, max, height = 150, format, highlightMax = true }) {
  const mx = max || Math.max(...data.map((d) => d[valueKey])) * 1.08;
  const peak = Math.max(...data.map((d) => d[valueKey]));
  return (
    <div className="chart-bars" style={{ height }}>
      {data.map((d, i) => {
        const h = Math.max(4, (d[valueKey] / mx) * 100);
        return (
          <div key={i} className="cbar-col">
            <span className="t-xs mono muted" style={{ fontSize: 10.5 }}>{format ? format(d[valueKey]) : d[valueKey]}</span>
            <div className="bar-track grow" style={{ width: "100%", display: "flex", alignItems: "flex-end", borderRadius: 5 }}>
              <div className={"cbar" + (highlightMax && d[valueKey] === peak ? " hi" : "")} style={{ height: h + "%" }} title={d.d || d.m} />
            </div>
            <span className="cbar-x" style={{ whiteSpace: "nowrap" }}>{d.d || d.m || d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- sparkline ---------- */
function Sparkline({ data, w = 240, h = 44, stroke = "var(--green)", fill = "var(--green-weak)" }) {
  const mx = Math.max(...data), mn = Math.min(...data);
  const rng = mx - mn || 1;
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - 4 - ((v - mn) / rng) * (h - 10)]);
  const line = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  const area = line + ` L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ height: h }}>
      <path d={area} fill={fill} opacity="0.55" />
      <path d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- confirm dialog ---------- */
function ConfirmDialog({ title, body, confirmLabel, tone = "pri", onConfirm, onClose, busy }) {
  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="scrim" onClick={onClose} />
      <div className="modal-card">
        <div className="modal-b">
          <div className="t-h2" style={{ marginBottom: 8 }}>{title}</div>
          <div className="t-body" style={{ lineHeight: 1.55 }}>{body}</div>
        </div>
        <div className="modal-f">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Keep it</button>
          <button className={`btn ${tone === "danger" ? "btn-danger" : "btn-pri"}`} onClick={onConfirm} disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- drawer ---------- */
function Drawer({ title, sub, onClose, children, footer, width }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" style={width ? { width } : null} role="dialog" aria-modal="true">
        <div className="drawer-h">
          <div>
            <div className="t-h2">{title}</div>
            {sub && <div className="t-xs muted" style={{ marginTop: 2 }}>{sub}</div>}
          </div>
          <button className="tb-icon" onClick={onClose} aria-label="Close"><HI.x s={18} /></button>
        </div>
        <div className="drawer-b">{children}</div>
        {footer && <div className="drawer-f">{footer}</div>}
      </aside>
    </>
  );
}

/* ---------- sidebar ---------- */
function Sidebar({ route, go, location, openLocSwitch, actionsCount, onLogout, user, isCollapsed }) {
  const [showLogout, setShowLogout] = useState(false);
  const userName = user?.name || H.host.name;
  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sb-top">
        <div className="sb-logo">
          <span className="mark">C</span>
          <span>CoCafe</span>
          <span className="sub">Host</span>
        </div>
        <button className="loc" onClick={openLocSwitch}>
          <img className="thumb" src={location.thumb} alt="" />
          <div className="grow">
            <div className="nm">{location.name}</div>
            <div className="mt">{location.area} · {location.status === "live" ? "Live" : "Paused"}</div>
          </div>
          <HI.chevD s={15} className="chev" />
        </button>
      </div>

      <nav className="sb-nav">
        {NAV.map((g) => (
          <div className="sb-group" key={g.group}>
            <div className="sb-group-h">{g.group}</div>
            {g.items.map((it) => {
              const Ico = HI[it.icon];
              const active = route === it.id;
              return (
                <a key={it.id} className={"sb-link" + (active ? " active" : "")} onClick={() => go(it.id)}>
                  <span className="sb-ic"><Ico s={17} /></span>
                  <span>{it.label}</span>
                  {it.dot && <span className="sb-dot" />}
                </a>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="sb-foot">
        <a className="sb-link" onClick={() => go("help")} style={{ marginBottom: 2 }}>
          <span className="sb-ic"><HI.help s={17} /></span><span>Help & resources</span>
        </a>
        <button className="sb-profile" onClick={() => setShowLogout(!showLogout)} style={{ border: "none", background: "none", cursor: "pointer", width: "100%" }}>
          <Av name={userName} c={0} size="md" />
          <div className="grow">
            <div className="nm">{userName}</div>
            <div className="role">{user?.role === 'host' ? 'Host' : 'Guest'} · {user?.superhost ? "Superhost" : user?.role === 'host' ? "Host" : "Member"}</div>
          </div>
          <HI.dots s={16} className="muted" />
          {showLogout && (
            <div style={{ position: "absolute", bottom: 60, left: 12, right: 12, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r)", padding: 8, zIndex: 10 }}>
              <button className="link t-sm" onClick={() => { console.log('🔴 SIDEBAR SIGN OUT CLICKED'); onLogout(); setShowLogout(false); }} style={{ width: "100%", textAlign: "left", padding: 8, color: "var(--red)", fontWeight: 600, cursor: 'pointer', border: 'none', background: 'none' }}>Sign out</button>
            </div>
          )}
        </button>
      </div>
    </aside>
  );
}

/* ---------- mobile nav drawer ---------- */
function MobileNavDrawer({ isOpen, onClose, route, go, actionsCount }) {
  const handleNavClick = (id) => {
    go(id);
    onClose();
  };

  return (
    <div className={`nav-drawer ${isOpen ? 'open' : ''}`}>
      <div className="scrim" onClick={onClose} />
      <div className="nav-drawer-content">
        <div className="nav-drawer-header">
          <div className="ttl">CoCafe Host</div>
          <button className="nav-drawer-close" onClick={onClose}>
            <HI.x s={18} />
          </button>
        </div>
        <nav className="sb-nav" style={{ flex: 1 }}>
          {NAV.map((g) => (
            <div className="sb-group" key={g.group}>
              <div className="sb-group-h">{g.group}</div>
              {g.items.map((it) => {
                const Ico = HI[it.icon];
                const active = route === it.id;
                return (
                  <a key={it.id} className={"sb-link" + (active ? " active" : "")} onClick={() => handleNavClick(it.id)}>
                    <span className="sb-ic"><Ico s={17} /></span>
                    <span>{it.label}</span>
                  </a>
                );
              })}
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
}

/* ---------- topbar ---------- */
const PAGE_TITLES = {
  dashboard: ["Dashboard", "Your space at a glance"],
  bookings: ["Bookings", "Manage reservations and guests"],
  availability: ["Availability", "Control when guests can book"],
  pricing: ["Pricing", "Rates, add-ons and offers"],
  workspace: ["Workspace", "Your listing details"],
  reviews: ["Reviews", "What guests are saying"],
  analytics: ["Revenue & analytics", "Performance and payouts"],
  help: ["Help & resources", "Guides and support"],
};

function Topbar({ route, notifications, onNotif, primaryAction, canGoBack, onBack, onMenuOpen, onLogout, onSidebarToggle }) {
  const [, sub] = PAGE_TITLES[route] || ["", ""];
  const unread = notifications.filter((n) => n.unread).length;
  const [openNotif, setOpenNotif] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenNotif(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toneColor = { green: "var(--green)", amber: "var(--amber)", clay: "var(--clay)", blue: "var(--blue)", red: "var(--red)" };
  return (
    <header className="topbar">
      <button className="hamburger-btn hamburger-desktop" onClick={onSidebarToggle} aria-label="Toggle sidebar" title="Toggle sidebar">
        <HI.menu s={20} />
      </button>
      <button className="hamburger-btn hamburger-mobile" onClick={onMenuOpen} aria-label="Open menu">
        <HI.menu s={20} />
      </button>
      <button className="tb-icon" onClick={onBack} aria-label="Go back" style={{ cursor: "pointer" }}>
        {HI.chevronLeft ? HI.chevronLeft({ s: 20 }) : "←"}
      </button>
      <div style={{ flex: 1 }} />
    </header>
  );
}

/* ---------- location switcher modal ---------- */
function LocationSwitcher({ current, locations, onPick, onClose, onAddSpace }) {
  return (
    <div className="modal">
      <div className="scrim" onClick={onClose} />
      <div className="modal-card" style={{ width: 460 }}>
        <div className="modal-b">
          <div className="row between" style={{ marginBottom: 4 }}>
            <div className="t-h2">Switch location</div>
            <button className="tb-icon" onClick={onClose}><HI.x s={18} /></button>
          </div>
          <div className="t-xs muted" style={{ marginBottom: 16 }}>You manage {locations.length} spaces under CoCafe.</div>
          <div className="col g8">
            {locations.map((l) => (
              <button key={l.id} className="row g12" onClick={() => onPick(l)}
                style={{ padding: 12, border: "1px solid " + (l.id === current.id ? "var(--green)" : "var(--border-2)"), borderRadius: "var(--r)", background: l.id === current.id ? "var(--green-weaker)" : "var(--surface)", textAlign: "left" }}>
                <img src={l.thumb} alt="" style={{ width: 44, height: 44, borderRadius: 9, objectFit: "cover" }} />
                <div className="grow">
                  <div className="row g8"><span className="t-h3">{l.name}</span>
                    {l.status === "paused" ? <span className="badge amber"><span className="bd" />Paused</span> : <span className="badge green"><span className="bd" />Live</span>}
                  </div>
                  <div className="t-xs muted" style={{ marginTop: 2 }}>{l.area}, {l.city} · {l.seats} desks · {l.rating}★</div>
                </div>
                {l.id === current.id && <HI.check s={18} className="green" />}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost btn-block" style={{ marginTop: 12 }} onClick={onAddSpace}><HI.plus s={16} /> Add a new space</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- toasts ---------- */
function Toasts({ items, dismiss }) {
  return (
    <div className="toasts">
      {items.map((t) => (
        <div key={t.id} className={"toast " + (t.kind || "ok")}>
          <span className="ti">{t.kind === "err" ? <HI.alert s={18} /> : <HI.checkCircle s={18} />}</span>
          <div className="grow">
            <div className="tt">{t.title}</div>
            {t.desc && <div className="td">{t.desc}</div>}
          </div>
          <button className="tx" onClick={() => dismiss(t.id)}><HI.x s={15} /></button>
        </div>
      ))}
    </div>
  );
}

/* ---------- section card header ---------- */
function CardHead({ title, sub, right }) {
  return (
    <div className="card-h">
      <div><div className="ttl">{title}</div>{sub && <div className="sub">{sub}</div>}</div>
      {right}
    </div>
  );
}

export {
  Av, Stars, StatusBadge, Delta, Kpi, Banner, Empty, SkeletonRows, BarChart, Sparkline,
  ConfirmDialog, Drawer, Sidebar, Topbar, LocationSwitcher, Toasts, CardHead, MobileNavDrawer, NAV, money,
};
